import { httpClient } from '@/api/http/client'

export type EditorGuideSettings = {
  count: number
  gutterWidth: number
  /** Spread-level checkpoint settings updated timestamp. */
  updatedAt?: string
}

type EditorGuideSettingsPayload = {
  count?: number | string | null
  gutterWidth?: number | string | null
  updatedAt?: string | null
}

type EditorGuideSettingsWriteResponse = {
  message?: string
}

type EditorGuideSettingsPutBody = {
  count: number
  gutterWidth: number
}

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

function basePath(spreadId: string): string {
  return `/checkpoints/${enc(spreadId)}/editorsettings`
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()

    if (trimmed === '') return null

    const parsed = Number(trimmed)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function normalizeGuideSettings(
  payload: EditorGuideSettingsPayload | null | undefined
): EditorGuideSettings {
  const countSource = toFiniteNumber(payload?.count)
  const count = countSource != null ? Math.max(0, Math.round(countSource)) : 0
  const gutterWidthSource = payload?.gutterWidth ?? 0
  const gutterWidthValue = toFiniteNumber(gutterWidthSource)
  const gutterWidth = gutterWidthValue != null ? Math.max(0, gutterWidthValue) : 0
  const updatedAt =
    typeof payload?.updatedAt === 'string' && payload.updatedAt.trim() !== ''
      ? payload.updatedAt
      : undefined

  return {
    count,
    gutterWidth,
    updatedAt,
  }
}

async function getColumnGuides(spreadId: string): Promise<EditorGuideSettings> {
  const response = await httpClient.get<EditorGuideSettingsPayload>(
    `${basePath(spreadId)}/columnguides`
  )

  return normalizeGuideSettings(response)
}

async function updateColumnGuides(
  spreadId: string,
  settings: EditorGuideSettings
): Promise<EditorGuideSettingsWriteResponse> {
  const payload: EditorGuideSettingsPutBody = {
    count: settings.count,
    gutterWidth: settings.gutterWidth,
  }

  return await httpClient.put<EditorGuideSettingsPutBody, EditorGuideSettingsWriteResponse>(
    `${basePath(spreadId)}/columnguides/update`,
    payload
  )
}

async function getRowGuides(spreadId: string): Promise<EditorGuideSettings> {
  const response = await httpClient.get<EditorGuideSettingsPayload>(
    `${basePath(spreadId)}/rowguides`
  )

  return normalizeGuideSettings(response)
}

async function updateRowGuides(
  spreadId: string,
  settings: EditorGuideSettings
): Promise<EditorGuideSettingsWriteResponse> {
  const payload: EditorGuideSettingsPutBody = {
    count: settings.count,
    gutterWidth: settings.gutterWidth,
  }

  return await httpClient.put<EditorGuideSettingsPutBody, EditorGuideSettingsWriteResponse>(
    `${basePath(spreadId)}/rowguides/update`,
    payload
  )
}

export const editorGuideSettingsService = {
  getColumnGuides,
  updateColumnGuides,
  getRowGuides,
  updateRowGuides,
}
